/**
 * Shared styling and base custom element class for dashboard info-cards.
 */

export const baseCardStyles = `
  :host {
    display: block;
    height: 100%;
    font-family: inherit;
  }

  .info-card {
    background: var(--p-card-background, var(--p-content-background, #ffffff));
    border: 1px solid var(--p-content-border-color, #e2e8f0);
    border-radius: var(--p-card-border-radius, 1rem);
    padding: 1.15rem;
    box-shadow: var(--p-card-shadow, 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05));
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    height: 100%;
    box-sizing: border-box;
  }

  .info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.12);
  }

  :host-context(.app-dark) .info-card,
  :host-context(.dark) .info-card,
  :host-context(.p-dark) .info-card,
  :host-context([data-theme="dark"]) .info-card,
  :host-context([theme="dark"]) .info-card,
  :host([theme="dark"]) .info-card {
    background: var(--p-card-background, var(--p-content-background, var(--p-surface-900, #18181b)));
    border-color: var(--p-content-border-color, var(--p-surface-700, #3f3f46));
    box-shadow: var(--p-card-shadow, 0 1px 3px 0 rgba(0, 0, 0, 0.3));
  }

  .card-top {
    display: flex;
    align-items: flex-start;
    gap: 0.875rem;
  }

  .icon-container {
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .icon-container svg {
    width: 1.75rem;
    height: 1.75rem;
    stroke-width: 2;
  }

  .card-body {
    flex: 1;
    min-width: 0;
  }

  .card-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--p-text-muted-color, #64748b);
    margin: 0 0 0.375rem 0;
  }

  :host-context(.app-dark) .card-title,
  :host-context(.dark) .card-title,
  :host-context(.p-dark) .card-title,
  :host-context([data-theme="dark"]) .card-title,
  :host-context([theme="dark"]) .card-title,
  :host([theme="dark"]) .card-title {
    color: var(--p-text-muted-color, #a1a1aa);
  }

  .card-value {
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.1;
    margin: 0 0 0.375rem 0;
    letter-spacing: -0.02em;
  }

  .card-subtitle {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--p-text-muted-color, #64748b);
    margin: 0;
  }

  :host-context(.app-dark) .card-subtitle,
  :host-context(.dark) .card-subtitle,
  :host-context(.p-dark) .card-subtitle,
  :host-context([data-theme="dark"]) .card-subtitle,
  :host-context([theme="dark"]) .card-subtitle,
  :host([theme="dark"]) .card-subtitle {
    color: var(--p-text-muted-color, #a1a1aa);
  }

  .card-divider {
    margin-top: 1rem;
    margin-bottom: 0.75rem;
    border-top: 1px solid var(--p-divider-border-color, var(--p-content-border-color, #e2e8f0));
  }

  :host-context(.app-dark) .card-divider,
  :host-context(.dark) .card-divider,
  :host-context(.p-dark) .card-divider,
  :host-context([data-theme="dark"]) .card-divider,
  :host-context([theme="dark"]) .card-divider,
  :host([theme="dark"]) .card-divider {
    border-top-color: var(--p-divider-border-color, var(--p-content-border-color, var(--p-surface-700, #3f3f46)));
  }

  .progress-container {
    margin-top: 1.25rem;
    width: 100%;
    height: 0.5rem;
    background-color: var(--p-progressbar-background, var(--p-content-border-color, #e2e8f0));
    border-radius: var(--p-progressbar-border-radius, 9999px);
    overflow: hidden;
  }

  :host-context(.app-dark) .progress-container,
  :host-context(.dark) .progress-container,
  :host-context(.p-dark) .progress-container,
  :host-context([data-theme="dark"]) .progress-container,
  :host-context([theme="dark"]) .progress-container,
  :host([theme="dark"]) .progress-container {
    background-color: var(--p-progressbar-background, var(--p-content-border-color, var(--p-surface-700, #3f3f46)));
  }

  .progress-bar {
    height: 100%;
    border-radius: var(--p-progressbar-border-radius, 9999px);
    transition: width 0.5s ease-in-out;
  }
`;

export class BaseInfoCard extends HTMLElement {
  constructor(template) {
    super();
    this.attachShadow({ mode: "open" });
    if (template) {
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
    this._data = null;
    this._themeObserver = null;
    this._setupClickHandler();
  }

  static get observedAttributes() {
    return ["theme", "lang"];
  }

  connectedCallback() {
    this._startThemeObserver();
    this.render();
  }

  disconnectedCallback() {
    this._stopThemeObserver();
  }

  attributeChangedCallback() {
    this.render();
  }

  get data() {
    return this._data;
  }

  set data(val) {
    this._data = val;
    this.render();
  }

  get defaultRoute() {
    return "";
  }

  get isHebrew() {
    return this._getLang() === "he";
  }

  _setupClickHandler() {
    const cardEl = this.shadowRoot.getElementById("card");
    if (cardEl) {
      cardEl.addEventListener("click", () => {
        const route = cardEl.getAttribute("data-route") || this.defaultRoute;
        if (route) {
          this.dispatchEvent(new CustomEvent("cardclick", {
            detail: { route },
            bubbles: true,
            composed: true
          }));
        }
      });
    }
  }

  _startThemeObserver() {
    this._stopThemeObserver();
    this._detectTheme();

    this._themeObserver = new MutationObserver(() => {
      this._detectTheme();
      this.render();
    });

    const targets = [document.documentElement, document.body, this.parentElement].filter(Boolean);
    targets.forEach((target) => {
      this._themeObserver.observe(target, {
        attributes: true,
        attributeFilter: ["class", "theme", "data-theme", "lang", "dir"]
      });
    });
  }

  _stopThemeObserver() {
    if (this._themeObserver) {
      this._themeObserver.disconnect();
      this._themeObserver = null;
    }
  }

  _detectTheme() {
    const docEl = document.documentElement;
    const bodyEl = document.body;
    const isDark =
      docEl.classList.contains("app-dark") ||
      docEl.classList.contains("dark") ||
      docEl.classList.contains("p-dark") ||
      bodyEl.classList.contains("app-dark") ||
      bodyEl.classList.contains("dark") ||
      bodyEl.classList.contains("p-dark") ||
      docEl.getAttribute("data-theme") === "dark" ||
      docEl.getAttribute("theme") === "dark" ||
      this.getAttribute("theme") === "dark";

    if (isDark) {
      this.setAttribute("theme", "dark");
    } else {
      this.removeAttribute("theme");
    }
  }

  _getLang() {
    const langAttr = this.getAttribute("lang") || document.documentElement.getAttribute("lang") || document.documentElement.lang || "he";
    const normalized = String(langAttr).toLowerCase();
    if (normalized.startsWith("en")) return "en";
    return "he";
  }

  render() {
    // Implemented by subclasses
  }
}
